import { create } from 'zustand';

import type { KioskAccountSessionClaim, User } from '@kangaroo/shared';

import {
  createKioskAccountTerminationLock,
  isKioskAccountSessionClaim,
  persistKioskAccountTerminationLock,
  restoreKioskAccountTerminationLock,
  type KioskClientTerminationReason,
} from '../lib/kioskAccountSession';
import { resetSessionState } from '../lib/resetSessionState';
import { setCircuitDiscoveryCacheScope } from '../lib/circuitDiscoveryCache';

const AUTH_STORAGE_KEY = 'gridline-auth';

export type AuthSessionKind = 'standard' | 'kiosk';

interface AuthSessionState {
  token: string | null;
  user: User | null;
  supportSession: StoredSupportSession | null;
  sessionKind: AuthSessionKind | null;
  kioskAccountSession: KioskAccountSessionClaim | null;
  kioskLastActivityAt: number | null;
  returnToKiosk: boolean;
  setSession: (token: string, user: User) => Promise<void>;
  setKioskSession: (
    token: string,
    user: User,
    accountSession: KioskAccountSessionClaim,
  ) => Promise<void>;
  startMasqueradeSession: (token: string, user: User) => Promise<void>;
  restoreSupportSession: () => Promise<void>;
  refreshSessionUser: (expectedToken: string, user: User) => Promise<void>;
  updateUser: (user: User) => void;
  markKioskActivity: (at: number) => void;
  clearSession: (reason?: KioskClientTerminationReason) => Promise<void>;
}

interface StoredSupportSession {
  token: string;
  user: User;
}

interface StoredAuthSession {
  token: string | null;
  user: User | null;
  supportSession?: StoredSupportSession | null;
  sessionKind?: AuthSessionKind | null;
  kioskAccountSession?: KioskAccountSessionClaim | null;
  kioskLastActivityAt?: number | null;
  returnToKiosk?: boolean;
}

function loadStoredSession(): StoredAuthSession {
  if (typeof window === 'undefined') {
    return { token: null, user: null };
  }

  const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) {
    return { token: null, user: null };
  }

  try {
    const stored = JSON.parse(raw) as StoredAuthSession;
    if (!stored.token || !stored.user) {
      return { token: null, user: null };
    }

    const sessionKind = stored.sessionKind === 'kiosk' ? 'kiosk' : 'standard';
    const kioskAccountSession =
      sessionKind === 'kiosk' && isKioskAccountSessionClaim(stored.kioskAccountSession)
        ? stored.kioskAccountSession
        : null;
    if (sessionKind === 'kiosk' && !kioskAccountSession) {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
      return { token: null, user: null, returnToKiosk: true };
    }
    const kioskLastActivityAt =
      sessionKind === 'kiosk' &&
      typeof stored.kioskLastActivityAt === 'number' &&
      Number.isFinite(stored.kioskLastActivityAt)
        ? stored.kioskLastActivityAt
        : sessionKind === 'kiosk'
          ? Date.now()
          : null;

    return { ...stored, sessionKind, kioskAccountSession, kioskLastActivityAt };
  } catch {
    return { token: null, user: null };
  }
}

function persistSession(session: StoredAuthSession) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

const initialSession = loadStoredSession();

if (initialSession.user?.id) {
  setCircuitDiscoveryCacheScope(initialSession.user.id);
}

export const useAuthStore = create<AuthSessionState>((set, get) => ({
  token: initialSession.token,
  user: initialSession.user,
  supportSession: initialSession.supportSession ?? null,
  sessionKind: initialSession.sessionKind ?? null,
  kioskAccountSession: initialSession.kioskAccountSession ?? null,
  kioskLastActivityAt: initialSession.kioskLastActivityAt ?? null,
  returnToKiosk: initialSession.returnToKiosk ?? false,
  setSession: async (token, user) => {
    const previousUserId = get().user?.id ?? null;
    if (previousUserId && previousUserId !== user.id) {
      await resetSessionState(previousUserId);
    }

    persistSession({
      token,
      user,
      supportSession: get().supportSession,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
    });
    setCircuitDiscoveryCacheScope(user.id);
    set({
      token,
      user,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
      returnToKiosk: false,
    });
  },
  setKioskSession: async (token, user, accountSession) => {
    const previousUserId = get().user?.id ?? null;
    if (previousUserId && previousUserId !== user.id) {
      await resetSessionState(previousUserId);
    }

    const kioskLastActivityAt = Date.now();
    persistSession({
      token,
      user,
      supportSession: null,
      sessionKind: 'kiosk',
      kioskAccountSession: accountSession,
      kioskLastActivityAt,
    });
    setCircuitDiscoveryCacheScope(user.id);
    set({
      token,
      user,
      supportSession: null,
      sessionKind: 'kiosk',
      kioskAccountSession: accountSession,
      kioskLastActivityAt,
      returnToKiosk: false,
    });
  },
  startMasqueradeSession: async (token, user) => {
    const current = get();
    const supportSession =
      current.supportSession ??
      (current.token && current.user ? { token: current.token, user: current.user } : null);
    const previousUserId = current.user?.id ?? null;

    if (previousUserId && previousUserId !== user.id) {
      await resetSessionState(previousUserId);
    }

    persistSession({
      token,
      user,
      supportSession,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
    });
    setCircuitDiscoveryCacheScope(user.id);
    set({
      token,
      user,
      supportSession,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
      returnToKiosk: false,
    });
  },
  restoreSupportSession: async () => {
    const supportSession = get().supportSession;
    if (!supportSession) {
      return;
    }

    const previousUserId = get().user?.id ?? null;
    if (previousUserId && previousUserId !== supportSession.user.id) {
      await resetSessionState(previousUserId);
    }

    persistSession({
      token: supportSession.token,
      user: supportSession.user,
      supportSession: null,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
    });
    setCircuitDiscoveryCacheScope(supportSession.user.id);
    set({
      token: supportSession.token,
      user: supportSession.user,
      supportSession: null,
      sessionKind: 'standard',
      kioskAccountSession: null,
      kioskLastActivityAt: null,
      returnToKiosk: false,
    });
  },
  refreshSessionUser: async (expectedToken, user) => {
    const beforeCleanup = get();
    if (beforeCleanup.token !== expectedToken) {
      return;
    }

    const previousUserId = beforeCleanup.user?.id ?? null;
    if (previousUserId && previousUserId !== user.id) {
      await resetSessionState(previousUserId);
    }

    const current = get();
    if (current.token !== expectedToken) {
      return;
    }

    persistSession({
      token: current.token,
      user,
      supportSession: current.supportSession,
      sessionKind: current.sessionKind,
      kioskAccountSession: current.kioskAccountSession,
      kioskLastActivityAt: current.kioskLastActivityAt,
    });
    setCircuitDiscoveryCacheScope(user.id);
    set({ user });
  },
  updateUser: (user) => {
    const current = useAuthStore.getState();
    persistSession({
      token: current.token,
      user,
      supportSession: current.supportSession,
      sessionKind: current.sessionKind,
      kioskAccountSession: current.kioskAccountSession,
      kioskLastActivityAt: current.kioskLastActivityAt,
    });
    set({ user });
  },
  markKioskActivity: (at) => {
    const current = get();
    if (current.sessionKind !== 'kiosk' || !current.token || !current.user) {
      return;
    }

    persistSession({
      token: current.token,
      user: current.user,
      supportSession: null,
      sessionKind: 'kiosk',
      kioskAccountSession: current.kioskAccountSession,
      kioskLastActivityAt: at,
    });
    set({ kioskLastActivityAt: at });
  },
  clearSession: async (reason = 'EXPLICIT_CLEAR') => {
    const current = get();
    const userId = current.user?.id ?? null;
    const returnToKiosk = current.sessionKind === 'kiosk';

    if (returnToKiosk && current.kioskAccountSession) {
      let storage: Storage | null = null;
      try {
        storage = window.localStorage;
      } catch {
        // The module-level lock still protects this running kiosk.
      }
      if (!restoreKioskAccountTerminationLock(storage)) {
        persistKioskAccountTerminationLock(
          storage,
          createKioskAccountTerminationLock(current.kioskAccountSession, reason),
        );
      }
    }

    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }

    set({
      token: null,
      user: null,
      supportSession: null,
      sessionKind: null,
      kioskAccountSession: null,
      kioskLastActivityAt: null,
      returnToKiosk,
    });

    try {
      await resetSessionState(userId, { kiosk: returnToKiosk });
    } catch {
      console.error('[Auth cleanup] Session cache cleanup failed.');
    }
  },
}));

export async function clearSessionForExpectedToken(expectedToken: string) {
  const current = useAuthStore.getState();
  if (current.token !== expectedToken) {
    return;
  }

  await current.clearSession();
}
